// ─────────────────────────────────────────────────────────────────────────────
// Token storage — the only localStorage access point for authentication tokens.
//
// All other environment data (settings, theme, user, selection) is stored
// automatically by environment.factory.ts through the Proxy on `environment`.
// ─────────────────────────────────────────────────────────────────────────────

export const AUTH_STORAGE_KEYS = {
    accessToken:                      'access_token',
    accessTokenTimestamp:             'access_token_timestamp',
    impersonatorAccessToken:          'impersonator_access_token',
    impersonatorSelectedOrganization: 'o-i-s',
    impersonatorSelectedBranch:       'b-i-s'
} as const;

export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];

/** Keys cleared on full sign-out */
export const AUTH_SESSION_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.impersonatorAccessToken,
    AUTH_STORAGE_KEYS.impersonatorSelectedOrganization,
    AUTH_STORAGE_KEYS.impersonatorSelectedBranch
] as const;

/** Keys cleared when a token refresh fails (keeps impersonation tokens intact) */
export const AUTH_REFRESH_FAILURE_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.impersonatorAccessToken
] as const;

/** Keys saved/restored around an impersonation session */
export const AUTH_IMPERSONATION_SELECTION_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.impersonatorSelectedOrganization,
    AUTH_STORAGE_KEYS.impersonatorSelectedBranch
] as const;

function hasStorage(): boolean {
    return typeof localStorage !== 'undefined';
}

/** Simple key/value store for authentication tokens. */
export const AuthStorage = {
    get(key: AuthStorageKey): string {
        return localStorage.getItem(key) ?? '';
    },

    set(key: AuthStorageKey, value: string): void {
        if (value) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    },

    remove(key: AuthStorageKey): void {
        localStorage.removeItem(key);
    },

    removeMany(keys: readonly AuthStorageKey[]): void {
        keys.forEach(key => localStorage.removeItem(key));
    },

    getJson<T>(key: AuthStorageKey): T | null {
        const raw = localStorage.getItem(key);
        try { return JSON.parse(raw) as T; }
        catch { localStorage.removeItem(key); return null; }
    },

    setJson(key: AuthStorageKey, value: unknown): void {
        try { localStorage.setItem(key, JSON.stringify(value)); }
        catch { /* quota exceeded */ }
    }
} as const;
